import * as React from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useMachine } from "@xstate/react";
import {
  Checkbox,
  ErrorMessage,
  Input,
  MobilePhoneNumberField,
  PostCodeField,
  REPLACE_SPACES,
  TextArea,
} from "@nju/form";
import { Icon } from "@nju/icons";
import { portalRoutes } from "@nju/routes";
import {
  A,
  Back,
  Button,
  ButtonsWrapper,
  Heading,
  Panel,
  Separator,
  Text,
  Tooltip,
} from "@nju/ui";
import { changeSimDeliveryAddressMachine } from "./changeSimDeliveryAddressForm.machine";

export function ChangeSimDeliveryAddressForm() {
  const router = useRouter();
  const params = portalRoutes.mojeKonto.mojaKarta.wymianaKarty.sim.adresDostawy
    .input(router.query)
    .unwrap();
  const [current, send] = useMachine(changeSimDeliveryAddressMachine, {
    actions: {
      setDeliveryAddress: ({
        street,
        streetNumber,
        apartmentNumber,
        postCode,
        municipality,
        country,
        contactNumber,
        note,
      }) => {
        window.localStorage.setItem(
          "sim_delivery_address",
          JSON.stringify({
            street,
            streetNumber,
            postCode,
            municipality,
            country,
            contactNumber,
            ...(note && { note }),
            ...(apartmentNumber && { apartmentNumber }),
          }),
        );

        router.push(
          portalRoutes.mojeKonto.mojaKarta.wymianaKarty.sim.potwierdzDane.get({
            ...(params?.userId && { userId: params.userId }),
          }),
        );
      },
    },
  });

  const { context, matches } = current;

  const {
    withoutStreet,
    streetError,
    streetNumberError,
    apartmentNumberError,
    postCodeError,
    municipalityError,
    contactNumberError,
    formError,
    street,
  } = context;

  const onInputChange: React.ChangeEventHandler<HTMLInputElement> =
    React.useCallback(
      ({ target }) => {
        const { name, value } = target;

        send({
          type: "SET_INPUT_VALUE",
          name,
          value,
        });
      },
      [send],
    );

  const onPhoneNumberChange = React.useCallback(
    (value: string) => {
      send({
        type: "SET_PHONE_NUMBER_VALUE",
        value: value.replace(REPLACE_SPACES, ""),
      });
    },
    [send],
  );

  const onPostCodeChange = React.useCallback(
    (value: string) => {
      send({ type: "SET_POSTCODE_VALUE", value });
    },
    [send],
  );

  const onTextChange = React.useCallback(
    (value: string) => {
      send({ type: "SET_TEXT_VALUE", value });
    },
    [send],
  );

  const onCheckboxValue = React.useCallback(
    (value: boolean) => {
      send({ type: "SET_WITHOUT_STREET", value });
    },
    [send],
  );

  const handleSubmit: React.FormEventHandler<HTMLFormElement> =
    React.useCallback(
      (event) => {
        event.preventDefault();
        send({ type: "SUBMIT" });
      },
      [send],
    );

  return (
    <Panel>
      <Back
        iconColor="accent"
        noPadding={true}
        text="wstecz"
        textTone="accent"
        type="LINK"
        url={portalRoutes.mojeKonto.mojaKarta.wymianaKarty.index.get({
          ...(params?.userId && { userId: params.userId }),
        })}
      />
      <Heading
        className="mb-[16px] from-tablet:mb-5 mt-5"
        font="ttfirs"
        tone="neutral"
        variant="h2"
        weight="bold"
      >
        gdzie mamy wysłać zamówienie?
      </Heading>
      <Text
        display="flex"
        size={{
          "@fromTablet": "m",
          "@onlyMobile": "s",
        }}
      >
        Nasze karty SIM kurier dostarczy w ciągu 1-2 dni roboczych. Dostawa jest
        bezpłatna.
      </Text>
      <Separator size="xs" tone="invisible" />
      <form onSubmit={handleSubmit}>
        <Input
          isDisabled={withoutStreet}
          label="ulica"
          name="street"
          onChange={onInputChange}
          state={streetError ? "error" : undefined}
          value={street}
        />
        {!withoutStreet && streetError && (
          <ErrorMessage message={streetError} testId="street-error-message" />
        )}
        <Separator size="xs" tone="invisible" />
        <div>
          <Checkbox
            defaultSelected={withoutStreet}
            name="withoutStreet"
            onValueChange={onCheckboxValue}
          >
            w mojej miejscowości nie ma nazw ulic
          </Checkbox>
        </div>
        <Separator size="xs" tone="invisible" />
        <div className="grid gap-5 from-desktop:grid-flow-col from-desktop:auto-cols-fr">
          <div>
            <Input
              label="numer domu"
              name="streetNumber"
              onChange={onInputChange}
              state={streetNumberError ? "error" : undefined}
            />
            {streetNumberError && (
              <ErrorMessage
                message={streetNumberError}
                testId="street-number-error-message"
              />
            )}
          </div>
          <div>
            <Input
              label="numer lokalu"
              name="apartmentNumber"
              onChange={onInputChange}
              state={apartmentNumberError ? "error" : undefined}
            />
            {apartmentNumberError && (
              <ErrorMessage
                message={apartmentNumberError}
                testId="apartment-number-error-message"
              />
            )}
          </div>
        </div>
        <Separator size="xs" tone="invisible" />
        <div className="grid gap-5 from-desktop:grid-flow-col from-desktop:auto-cols-fr">
          <div>
            <Input
              label="miasto"
              name="municipality"
              onChange={onInputChange}
              state={municipalityError ? "error" : undefined}
            />
            {municipalityError && (
              <ErrorMessage
                message={municipalityError}
                testId="city-error-message"
              />
            )}
          </div>
          <div>
            <PostCodeField
              onValueChange={onPostCodeChange}
              state={postCodeError ? "error" : undefined}
            />
            {postCodeError && (
              <ErrorMessage
                message={postCodeError}
                testId="post-code-error-message"
              />
            )}
          </div>
        </div>
        <Separator size="xs" tone="invisible" />
        <div className="grid gap-5 from-desktop:grid-flow-col from-desktop:auto-cols-fr">
          <div>
            <MobilePhoneNumberField
              label="numer telefonu"
              onValueChange={onPhoneNumberChange}
              state={contactNumberError ? "error" : undefined}
              suffix={
                <Tooltip content="Wpisz numer telefonu komórkowego. Podany numer będzie służył wyłącznie do kontaktu z Tobą w sprawie dostawy karty SIM.">
                  <Icon name="info-circle" />
                </Tooltip>
              }
            />
            {contactNumberError && (
              <ErrorMessage
                message={contactNumberError}
                testId="contact-number-error-message"
              />
            )}
          </div>
          <div>
            <Input
              background="white"
              isReadOnly={true}
              label="kraj"
              name="country"
              value="Polska"
            />
          </div>
        </div>
        <Separator size="xs" tone="invisible" />
        <TextArea
          label="wiadomość dla kuriera (opcjonalnie)"
          maxLength={100}
          name="note"
          onChange={onTextChange}
          placeholder={
            "wiadomość dla kuriera (opcjonalnie) np. 2 piętro, klatka C lub alternatywny numer telefonu kontaktowego"
          }
          rows={5}
        />
        <Separator size="xs" tone="invisible" />
        <ButtonsWrapper withSeparator={false}>
          <Link
            href={portalRoutes.mojeKonto.mojaKarta.index.get({
              ...(params?.userId && { userId: params.userId }),
            })}
            passHref={true}
          >
            <A size={{ "@onlyMobile": "s" }} variant="secondary">
              anuluj
            </A>
          </Link>
          <Button
            isLoading={!matches("ready")}
            size={{ "@onlyMobile": "s" }}
            type="submit"
          >
            dalej
          </Button>
        </ButtonsWrapper>
        {formError && (
          <>
            <Separator size="xs" tone="invisible" />
            <ErrorMessage message={formError} />
          </>
        )}
      </form>
    </Panel>
  );
}
